import {
  AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, HostListener, NgZone, computed, inject, input, output, viewChild
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { LembreteNotificacao } from '../../../model/lembrete/lembrete-notificacao.model';
import { MethodsUtils } from '../../../util/methods.utils';
import { CelularMaskPipe } from '../../../pipe/celular-mask.pipe';
import {
  CURVA_ANIMACAO_LEMBRETE, DURACAO_ABERTURA_MODAL_LEMBRETE_MS, DURACAO_SAIDA_MODAL_LEMBRETE_MS, OPCOES_ADIAMENTO_MINUTOS_LEMBRETE
} from '../../../util/lembrete-notificacao.constantes';
import { ROTULOS_DIA_DESTACADOS_LEMBRETE } from './utils/lembrete-notificacao-modal.constantes';

@Component({
  selector: 'app-lembrete-notificacao-modal',
  standalone: true,
  imports: [DatePipe, CelularMaskPipe],
  templateUrl: './lembrete-notificacao-modal.component.html',
  styleUrl: './lembrete-notificacao-modal.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LembreteNotificacaoModalComponent implements AfterViewInit {
  readonly notificacao = input.required<LembreteNotificacao>();
  readonly mobile = input(false);
  readonly origem = input<DOMRect | null>(null);
  readonly acionavel = input(true);

  readonly fechar = output<DOMRect | null>();
  readonly adiar = output<number>();
  readonly concluir = output<void>();
  readonly dispensar = output<void>();
  readonly abrirContato = output<DOMRect | null>();

  private readonly zona = inject(NgZone);
  private readonly caixa = viewChild.required<ElementRef<HTMLElement>>('caixa');
  private readonly fundo = viewChild.required<ElementRef<HTMLElement>>('fundo');

  readonly opcoesAdiamento = OPCOES_ADIAMENTO_MINUTOS_LEMBRETE;
  readonly dataHora = computed(() => new Date(this.notificacao().dataHora));
  readonly fotoContato = computed(() =>
    MethodsUtils.fotoPerfilContato(this.notificacao().contatoFotoPerfilUrl, this.notificacao().contatoStatus));
  readonly rotuloDia = computed(() => {
    const rotulo = MethodsUtils.rotuloDia(this.dataHora());
    return ROTULOS_DIA_DESTACADOS_LEMBRETE.includes(rotulo) ? rotulo : null;
  });

  private saindo = false;

  ngAfterViewInit(): void {
    this.animarEntrada();
  }

  @HostListener('document:keydown.escape')
  aoPressionarEsc(): void {
    this.aoFechar();
  }

  rotuloOpcao(minutos: number): string {
    return minutos >= 60 ? `${minutos / 60} hora` : `${minutos} min`;
  }

  marcarFotoIndisponivel(): void {
    MethodsUtils.marcarFotoPerfilIndisponivel(this.notificacao().contatoFotoPerfilUrl);
  }

  aoFechar(): void {
    this.retornarAoCard(retorno => this.fechar.emit(retorno));
  }

  aoAbrirContato(): void {
    this.retornarAoCard(retorno => this.abrirContato.emit(retorno));
  }

  aoDispensar(): void {
    this.executar(() => this.dispensar.emit());
  }

  aoConcluir(): void {
    this.executar(() => this.concluir.emit());
  }

  aoAdiar(minutos: number): void {
    this.executar(() => this.adiar.emit(minutos));
  }

  private retornarAoCard(acao: (retorno: DOMRect | null) => void): void {
    if (this.saindo) return;
    if (this.mobile() || !this.origem()) {
      this.executar(() => acao(null));
      return;
    }
    this.saindo = true;
    acao(this.caixa().nativeElement.getBoundingClientRect());
  }

  private executar(acao: () => void): void {
    if (this.saindo) return;
    this.saindo = true;
    this.animarSaida().then(() => this.zona.run(acao));
  }

  private animarEntrada(): void {
    const duracao = MethodsUtils.duracaoAnimacao(DURACAO_ABERTURA_MODAL_LEMBRETE_MS);
    const caixa = this.caixa().nativeElement;
    this.fundo().nativeElement.animate([{ opacity: 0 }, { opacity: 1 }], { duration: duracao / 2, easing: 'ease-out' });

    if (this.mobile()) {
      caixa.animate([{ transform: 'translateY(-100%)' }, { transform: 'translateY(0)' }], { duration: duracao, easing: CURVA_ANIMACAO_LEMBRETE });
      return;
    }

    const origem = this.origem();
    if (!origem) {
      caixa.animate([{ opacity: 0, transform: 'scale(.92)' }, { opacity: 1, transform: 'scale(1)' }], { duration: duracao, easing: CURVA_ANIMACAO_LEMBRETE });
      return;
    }

    caixa.animate(
      [
        { transform: MethodsUtils.transformacaoFlip(origem, caixa.getBoundingClientRect()), opacity: 0.6 },
        { transform: 'translate(0, 0) scale(1)', opacity: 1 }
      ],
      { duration: duracao, easing: CURVA_ANIMACAO_LEMBRETE }
    );
  }

  private animarSaida(): Promise<unknown> {
    const opcoes: KeyframeAnimationOptions = {
      duration: MethodsUtils.duracaoAnimacao(DURACAO_SAIDA_MODAL_LEMBRETE_MS),
      easing: 'ease-in',
      fill: 'forwards'
    };
    this.fundo().nativeElement.animate([{ opacity: 1 }, { opacity: 0 }], opcoes);
    const quadros = this.mobile()
      ? [{ transform: 'translateY(0)' }, { transform: 'translateY(-100%)' }]
      : [{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(.96)' }];
    return this.caixa().nativeElement.animate(quadros, opcoes).finished;
  }
}
